import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space, message} from "antd";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {CreateSubscriptionOfferingForm} from "../../../../components/offering-form/subscriptions";
import {FormWrapper} from "../../../../components/form-wrapper";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useGetEditableSubscriptionContext} from "../context/get-editable-subscription";
import {useDeleteSubscriptionFromEditHook} from "../hooks/delete-subscription";
import {editSubscriptionBasicFormValidationSchema} from "../schema-validation";
import type {EditSubscriptionBasicFormValues} from "../types";
import {mapSubscriptionToBasicFormValues} from "../map-subscription-to-form";
import type {UpdateSubscriptionApiPayload} from "../../../../react-query-hooks/store/subscriptions/update-subscription";
import {useUpdateSubscriptionApi} from "../../../../react-query-hooks/store/subscriptions/update-subscription";
import {useAddSubscriptionTiersApi} from "../../../../react-query-hooks/store/subscriptions/add-subscription-tiers";
import {useUpdateSubscriptionTiersApi} from "../../../../react-query-hooks/store/subscriptions/update-subscription-tiers";
import {syncEditSubscriptionTiers} from "../sync-edit-subscription-tiers";
import {useState} from "react";

export function EditSubscriptionBasicForm() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const {
    state: {subscriptionData},
  } = useGetEditableSubscriptionContext();
  const subId = subscriptionData.productSubscription.id;
  const updateSubscription = useUpdateSubscriptionApi();
  const addTiers = useAddSubscriptionTiersApi();
  const updateTiers = useUpdateSubscriptionTiersApi();
  const {deleteSubscription} = useDeleteSubscriptionFromEditHook(subId);

  const formMethods = useForm<EditSubscriptionBasicFormValues>({
    resolver: yupResolver(editSubscriptionBasicFormValidationSchema),
    mode: "onSubmit",
    defaultValues: mapSubscriptionToBasicFormValues(subscriptionData),
  });

  async function onSubmit(values: EditSubscriptionBasicFormValues) {
    const hasTierChanges = !!formMethods.formState.dirtyFields.tiers;
    const payload: UpdateSubscriptionApiPayload = {
      name: values.name.trim(),
      description: values.description,
      hideFromProfile: values.hideFromProfile ?? false,
      requestPhoneNumber: values.requestCustomerPhoneNumber ?? false,
      enableCurrencyConversion: true,
      selectedCurrencies: values.selected_currencies ?? [],
    };
    if (values.isUrlRedirect && values.url_redirect?.trim()) {
      payload.redirectUrl = values.url_redirect.trim();
    }

    setIsSaving(true);
    try {
      await updateSubscription.mutateAsync({subscriptionId: subId, payload});
      if (hasTierChanges) {
        await syncEditSubscriptionTiers({
          subscriptionId: subId,
          formTiers: values.tiers ?? [],
          originalTiers: subscriptionData.productSubscription.tiers,
          addTiers: addTiers.mutateAsync,
          updateTiers: updateTiers.mutateAsync,
        });
      }
      message.success("Updated successfully");
      navigate("/products?activeTab=subscriptions");
    } catch {
      // Mutation hooks already surface errors
    } finally {
      setIsSaving(false);
    }
  }

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name || formMethods.formState.errors.description;
  const isTiersError = !!formMethods.formState.errors.tiers;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.url_redirect;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
        <Space direction="vertical" size={16} style={{display: "flex"}}>
          <FormWrapper borderless>
            <CreateSubscriptionOfferingForm
              isGeneralInformationError={!!isGeneralInformationError}
              isTiersError={isTiersError}
              isSettingsError={!!isSettingsError}
            />
          </FormWrapper>
          {isFormError && (
            <Alert
              message="Please fill in the required fields above, outlined in red"
              type="warning"
            />
          )}
          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <StyledServiceButtonWrapper>
                <Button
                  className="secondary-btn"
                  onClick={() => navigate("/products?activeTab=subscriptions")}
                  block
                  size="small"
                  type="default"
                >
                  Back
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="small"
                  disabled={isFormError}
                  loading={isSaving}
                >
                  Update subscription
                </Button>
                <OfferingDeleteButton
                  cancelText="Exit"
                  content="Are you sure you want to delete this subscription? This action cannot be undone."
                  okText="Delete subscription"
                  onOk={deleteSubscription}
                  title="Confirm subscription deletion"
                />
              </StyledServiceButtonWrapper>
            </Col>
          </Row>
        </Space>
      </form>
    </FormProvider>
  );
}
